/**
 * The audience.
 *
 * Search matches names, addresses and attribute values in one index, because
 * the thing someone has when they come here is rarely a full name — it is a
 * bounce report with an address on it.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { api, ApiError, download } from "../api/client";
import { useAsync } from "../hooks";
import { SkeletonRows } from "../components/Loading";
import { Consent, isSendable } from "../components/Consent";
import { ContactDrawer } from "./ContactDrawer";
import { IMPORT_LIST_LIMIT, ImportDrawer, ImportList, isInProgress } from "./ImportHistory";
import { Icon, type IconName } from "../components/Sprites";
import { ImportDialog } from "./ImportContacts";
import { useSession } from "../session";
import type { Channel, Contact, ContactImport, ContactTag, CursorPage, Group } from "../api/types";

const PAGE_SIZE = 50;

/**
 * The two tabs, each a route: `/contacts` and `/contacts/imports`, so either
 * can be linked to, and an import at `/contacts/imports/:importId`.
 */
export type ContactsView = "contacts" | "imports";

const CHANNEL_ICON: Record<Channel, IconName> = {
  email: "mail",
  sms: "sms",
  push: "push",
  whatsapp: "sms",
  webhook: "external",
};

export function Contacts({ view = "contacts" }: { view?: ContactsView }) {
  const { orgId, can } = useSession();
  const navigate = useNavigate();
  const { contactId, importId } = useParams();

  // Filters live in the URL so a filtered view can be pasted into a ticket.
  const [params, setParams] = useSearchParams();
  const search = params.get("search") ?? "";
  const channel = params.get("channel") ?? "";
  const consent = params.get("consent") ?? "";
  const tagId = params.get("tag_id") ?? "";
  const groupId = params.get("group_id") ?? "";

  const [history, setHistory] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const cursor = history.at(-1) ?? "";

  const contacts = useAsync<CursorPage<Contact>>(
    (signal) =>
      api.get<CursorPage<Contact>>("/v1/contacts", {
        signal,
        query: { limit: PAGE_SIZE, cursor, search, channel, consent, tag_id: tagId, group_id: groupId },
      }),
    [orgId, cursor, search, channel, consent, tagId, groupId],
  );

  const tags = useAsync<ContactTag[]>(
    (signal) => api.get<ContactTag[]>("/v1/tags", { signal }),
    [orgId],
  );

  // Fetched for the filter, and shared with the bulk action below so a group
  // list is not requested twice on one screen.
  const groups = useAsync<CursorPage<Group>>(
    (signal) => api.get<CursorPage<Group>>("/v1/groups", { query: { limit: 100 }, signal }),
    [orgId],
  );

  // Fetched on both tabs: the Imports tab lists them, and its label carries the
  // count — or says an import is running — from the other.
  const imports = useAsync<ContactImport[]>(
    (signal) => api.get<ContactImport[]>("/v1/contacts/imports", { signal }),
    [orgId],
  );

  // While an import is queued or running, the screen follows it: the tab says
  // so, and when it finishes the contacts it made appear without anybody
  // pressing Refresh. Nothing polls when nothing is running.
  const inProgress = (imports.data ?? []).some(isInProgress);
  const wasInProgress = useRef(false);
  const { reload: reloadImports } = imports;
  const { reload: reloadContacts } = contacts;
  const { reload: reloadTags } = tags;
  useEffect(() => {
    if (wasInProgress.current && !inProgress) {
      reloadContacts();
      reloadTags();
    }
    wasInProgress.current = inProgress;
    if (!inProgress) return;
    const timer = window.setInterval(reloadImports, 3000);
    return () => window.clearInterval(timer);
  }, [inProgress, reloadImports, reloadContacts, reloadTags]);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setHistory([]);
  };

  const rows = contacts.data?.data ?? [];
  const firstIndex = history.length * PAGE_SIZE + 1;

  // The export carries the current filters, so the file matches the screen.
  const exportPath = `/v1/contacts/export?${new URLSearchParams(
    Object.entries({ search, channel, consent, tag_id: tagId, group_id: groupId }).filter(([, v]) => v),
  )}`;

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const runExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await download(exportPath, "contacts.csv");
    } catch (err) {
      setExportError(err instanceof ApiError ? err.message : "Could not build the export.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="topbar">
        <h1 className="oc-headline-lg">Contacts</h1>
        <div className="row">
          {/* Contacts search, so it goes with the contacts tab. Left on the
              Imports tab it would search people from a screen showing files. */}
          {view === "contacts" && (
            <div className="oc-search" style={{ width: 320 }}>
              <Icon name="search" className="oc-search__icon" />
              <input
                className="oc-input"
                data-screen-search
                placeholder="Name, address, or any attribute…"
                defaultValue={search}
                onKeyDown={(event) => {
                  if (event.key === "Enter") setFilter("search", event.currentTarget.value.trim());
                }}
              />
            </div>
          )}
          {can("contacts:write") && (
            <button type="button" className="oc-btn oc-btn--primary" onClick={() => setImporting(true)}>
              <Icon name="plus" size={12} />
              Import CSV
            </button>
          )}
        </div>
      </div>

      <ContactsTabs view={view} imports={imports.data} />

      {view === "imports" && (
        // The same toolbar row the contacts tab has, holding only Refresh, so
        // the table starts at the same height on both tabs and switching
        // between them does not make the page jump.
        <div className="filters">
          <span className="grow" />
          <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={imports.reload}>
            <Icon name="refresh" size={12} />
            Refresh
          </button>
        </div>
      )}

      {view === "contacts" && (
        <div className="filters">
          <FilterSelect
            label="Channel"
            value={channel}
            options={["email", "sms", "push", "whatsapp", "webhook"]}
            onChange={(value) => setFilter("channel", value)}
          />
          <FilterSelect
            label="Consent"
            value={consent}
            options={["subscribed", "pending", "unsubscribed", "unknown"]}
            onChange={(value) => setFilter("consent", value)}
          />
          {(tags.data?.length ?? 0) > 0 && (
            <FilterSelect
              label="Tag"
              value={tagId}
              options={(tags.data ?? []).map((t) => ({
                value: t.id,
                label: `${t.name} (${t.contact_count ?? 0})`,
              }))}
              onChange={(value) => setFilter("tag_id", value)}
            />
          )}
          {/* Hidden until there is a group to pick, matching how the tag filter
              behaves — an empty select is a control that looks broken. */}
          {(groups.data?.data.length ?? 0) > 0 && (
            <FilterSelect
              label="Group"
              value={groupId}
              options={(groups.data?.data ?? []).map((g) => ({
                value: g.id,
                label: `${g.name} (${g.member_count})`,
              }))}
              onChange={(value) => setFilter("group_id", value)}
            />
          )}

          {(search || channel || consent || tagId || groupId) && (
            <>
              <span className="filters__div" />
              <button
                type="button"
                className="oc-btn oc-btn--ghost oc-btn--sm"
                onClick={() => {
                  setParams(new URLSearchParams(), { replace: true });
                  setHistory([]);
                }}
              >
                Clear all
              </button>
            </>
          )}

          <span className="grow" />
          {/* This was an <a href> and had been answering 401 since it shipped.
              Its comment argued the case well — "a real navigation rather than
              fetch: the browser handles the streamed download, and a
              multi-megabyte export never enters JS memory" — and every word of
              that is true of a request that never arrives. A plain navigation
              sends no custom headers, and X-OpenComms-Org is the only thing the
              session middleware reads to know which organization is asking.

              The streaming argument was real and is the cost of the fix: the
              whole CSV is now a blob in memory before it is saved. That is worth
              it against a button that does not work, and the honest version of
              the old comment is that nobody had clicked it. */}
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--sm"
            disabled={exporting}
            onClick={() => void runExport()}
          >
            <Icon name="external" size={12} />
            {exporting ? "Preparing…" : "Export CSV"}
          </button>
          <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={contacts.reload}>
            <Icon name="refresh" size={12} />
            Refresh
          </button>
          {exportError && <span className="field-error">{exportError}</span>}
        </div>
      )}

      <div className="content">
        {view === "imports" ? (
          <ImportList
            imports={imports}
            selectedId={importId}
            onOpen={(id) => navigate(`/contacts/imports/${id}${window.location.search}`)}
          />
        ) : (
          <div className="oc-panel oc-starfield" style={{ flex: 1, minWidth: 0 }}>
            <div
              className="oc-table contacts-table"
              style={{ flex: 1, minHeight: 0 }}
            >
              <div className="oc-table__head" role="row">
                <span>Name</span>
                <span>Addresses</span>
                <span>Consent</span>
                <span>Tags</span>
                <span className="oc-table__cell--right">Added</span>
              </div>

              <div className="oc-table__body">
                {contacts.error && (
                  <div className="oc-empty">
                    <div className="oc-empty__title">Could not load contacts</div>
                    <div className="oc-empty__hint">{contacts.error.message}</div>
                  </div>
                )}

                {contacts.loading && rows.length === 0 && <SkeletonRows columns={5} />}

                {!contacts.error && rows.length === 0 && !contacts.loading && (
                  <NoContacts filtered={Boolean(search || channel || consent || tagId || groupId)} />
                )}

                {rows.map((contact) => (
                  <Row
                    key={contact.id}
                    contact={contact}
                    selected={contact.id === contactId}
                    onOpen={() => navigate(`/contacts/${contact.id}${window.location.search}`)}
                  />
                ))}
              </div>

              <div className="oc-table__footer">
                <span className="oc-mono-sm">
                  {summary(rows.length, firstIndex, contacts.data?.estimated_total)}
                </span>
                <span style={{ display: "flex", gap: "var(--oc-space-2)" }}>
                  <button
                    type="button"
                    className="oc-btn oc-btn--ghost oc-btn--sm"
                    disabled={history.length === 0}
                    onClick={() => setHistory((h) => h.slice(0, -1))}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="oc-btn oc-btn--ghost oc-btn--sm"
                    disabled={!contacts.data?.has_more || !contacts.data.next_cursor}
                    onClick={() => {
                      const next = contacts.data?.next_cursor;
                      if (next) setHistory((h) => [...h, next]);
                    }}
                  >
                    Next
                  </button>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {contactId && (
        <ContactDrawer
          contactId={contactId}
          onClose={() => navigate(`/contacts${window.location.search}`)}
          onChanged={contacts.reload}
        />
      )}

      {importId && (
        <ImportDrawer
          importId={importId}
          onClose={() => navigate(`/contacts/imports${window.location.search}`)}
        />
      )}

      {importing && (
        <ImportDialog
          onClose={() => {
            setImporting(false);
            // Closing mid-run leaves the import going in the background; the
            // reload is what lets the tab notice it and follow it to the end.
            imports.reload();
          }}
          onFinished={() => {
            contacts.reload();
            tags.reload();
            imports.reload();
          }}
        />
      )}
    </>
  );
}

/**
 * All contacts | Imports.
 *
 * Links rather than buttons, because each tab is a route. Both carry the query
 * string, so a filtered contact list is still filtered after a look at the
 * imports — the Imports tab ignores it.
 */
function ContactsTabs({ view, imports }: { view: ContactsView; imports: ContactImport[] | null }) {
  const [params] = useSearchParams();
  const search = params.toString() ? `?${params}` : "";

  const running = (imports ?? []).filter(isInProgress).length;
  const count = imports?.length ?? 0;

  return (
    <div className="view-tabs">
      <div className="tabs" role="tablist" aria-label="Contacts">
        <Link
          to={{ pathname: "/contacts", search }}
          role="tab"
          aria-selected={view === "contacts"}
          className="tabs__tab"
        >
          All contacts
        </Link>
        <Link
          to={{ pathname: "/contacts/imports", search }}
          role="tab"
          aria-selected={view === "imports"}
          className="tabs__tab"
        >
          Imports
          {/* A running import outranks the count: it is the one thing on this
              tab that is changing, and somebody who closed the dialog mid-run
              is looking for exactly that. */}
          {running > 0 ? (
            <span className="tabs__count">{running} running</span>
          ) : (
            count > 0 && (
              <span className="tabs__count">
                {count >= IMPORT_LIST_LIMIT ? `${IMPORT_LIST_LIMIT}+` : count}
              </span>
            )
          )}
        </Link>
      </div>
    </div>
  );
}

function Row({
  contact,
  selected,
  onOpen,
}: {
  contact: Contact;
  selected: boolean;
  onOpen: () => void;
}) {
  const added = new Date(contact.created_at);

  return (
    <div
      className="oc-table__row"
      role="row"
      tabIndex={0}
      aria-selected={selected || undefined}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <span className="oc-body-sm oc-truncate">{contact.name || <span className="oc-text-muted">No name</span>}</span>

      <span style={{ minWidth: 0 }}>
        {contact.identities.map((identity) => (
          <span key={identity.id} className="row" style={{ gap: 6, minWidth: 0 }}>
            <Icon name={CHANNEL_ICON[identity.channel] ?? "mail"} className="channel-icon" />
            <span
              className="oc-mono-sm oc-truncate"
              style={{
                // Muted when a campaign would not reach it. The address is
                // still shown — it is how you find the person — but it should
                // not read as reachable when it is not.
                color: isSendable(identity.consent_status)
                  ? "var(--oc-accent-bright)"
                  : "var(--oc-text-muted-aa)",
              }}
            >
              {identity.address}
            </span>
          </span>
        ))}
        {contact.identities.length === 0 && (
          <span className="oc-mono-sm oc-text-muted">no address</span>
        )}
      </span>

      {/* One badge per identity, never one per person: consent is per address,
          and a single badge would be a lie about the model. */}
      <span style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
        {contact.identities.map((identity) => (
          <Consent key={identity.id} status={identity.consent_status} />
        ))}
      </span>

      <span className="oc-body-sm oc-truncate oc-text-secondary">
        {(contact.tags ?? []).map((t) => t.name).join(", ") || "—"}
      </span>

      <span className="oc-mono-sm oc-text-secondary oc-table__cell--right" title={added.toISOString()}>
        {added.toLocaleDateString()}
      </span>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<string | { value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <label className="oc-chip" style={{ paddingRight: 4 }}>
      {label}:
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={{
          background: "transparent",
          border: "none",
          color: "inherit",
          font: "inherit",
          letterSpacing: "inherit",
          textTransform: "inherit",
        }}
      >
        <option value="">any</option>
        {options.map((option) => {
          const value = typeof option === "string" ? option : option.value;
          const text = typeof option === "string" ? option : option.label;
          return (
            <option key={value} value={value}>
              {text}
            </option>
          );
        })}
      </select>
    </label>
  );
}

// The curl half is addressed to whoever can mint the credential it uses, for
// the reason NoMessages sets out at length: `keys:manage` is a developer
// capability, and a marketing member shown `$OC_API_KEY` has no way to obtain
// one and no API Keys screen to look on.
//
// What survives for everybody is the sentence about consent. It is the thing
// this product is most opinionated about, it explains the columns they are
// about to see, and it is true whoever is reading.
function NoContacts({ filtered }: { filtered: boolean }) {
  const { can } = useSession();

  if (filtered) {
    return (
      <div className="oc-empty">
        <div className="oc-empty__title">No contacts match these filters</div>
        <div className="oc-empty__hint">Clear a filter to widen the search.</div>
      </div>
    );
  }

  if (!can("keys:manage")) {
    return (
      <div className="oc-empty">
        <div className="oc-empty__title">No contacts yet</div>
        <div className="oc-empty__hint">
          Import a CSV to get started, or publish a signup form and let it fill itself. Consent is
          recorded per address, so the same person can be subscribed on SMS and unsubscribed on
          email.
        </div>
      </div>
    );
  }

  return (
    <div className="oc-empty">
      <div className="oc-empty__title">No contacts yet</div>
      <div className="oc-empty__hint">
        Import a CSV, or create one through the API. Consent is recorded per address, so the same
        person can be subscribed on SMS and unsubscribed on email.
      </div>
      <pre className="oc-code">{`curl ${window.location.origin}/v1/contacts \\
  -H "Authorization: Bearer $OC_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Ada Lovelace",
    "identities": [{
      "channel": "email",
      "address": "ada@example.com",
      "consent_status": "subscribed",
      "consent_source": "form"
    }]
  }'`}</pre>
    </div>
  );
}

function summary(count: number, firstIndex: number, estimatedTotal: number | null | undefined): string {
  if (count === 0) return "No contacts";

  const range = `${firstIndex.toLocaleString()}–${(firstIndex + count - 1).toLocaleString()}`;
  if (estimatedTotal === null || estimatedTotal === undefined) return `Showing ${range}`;
  return `Showing ${range} of ${estimatedTotal.toLocaleString()} contacts`;
}
